import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Grid, makeStyles } from '@material-ui/core'
import { TextField } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import DeleteIcon from '@material-ui/icons/Delete'
import { FormattedMessage } from 'react-intl'
import EmailValidator from 'email-validator'
import { CapeeshColors } from '../../assets/ColorPalette'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'

const norwegianPhoneRegex = /^\+?47?\d{8}$/

const validatePhoneNumber = (phone) => {
    const cleaned = (phone || '').replace(/[\s-]/g, '')
    if (!cleaned) return true
    return norwegianPhoneRegex.test(cleaned)
}

const normalizePhoneNumber = (phone) => {
    const cleaned = (phone || '').replace(/[\s-]/g, '')
    if (!cleaned) return ''
    if (cleaned.startsWith('+47')) return cleaned
    if (cleaned.startsWith('47') && cleaned.length === 10) return '+' + cleaned
    if (cleaned.length === 8) return '+47' + cleaned
    return cleaned
}

const useStyles = makeStyles((theme) => ({}))

const MultipleStudentAddComponent = ({
    role = 'Student',
    onStudentRowsChanged,
    onInviteStudentsToSchool,
    showTitle,
    title,
    description,
    initialStudentRows = [],
}) => {
    const [rowInFocus, setRowInFocus] = useState(0)

    const emptyRow = { index: 0, email: '', phoneNumber: '', name: '', validEmail: false, validPhone: true }
    const tempStudentRowsToAdd =
        initialStudentRows?.length > 0 ? initialStudentRows : [emptyRow]
    const [studentRowsToAdd, setStudentRowsToAdd] = React.useState(tempStudentRowsToAdd)

    useEffect(() => {
        const lastRow = studentRowsToAdd[studentRowsToAdd.length - 1]
        if (lastRow && (lastRow.email || lastRow.phoneNumber)) {
            const newIndex = studentRowsToAdd.length
            setStudentRowsToAdd((prev) => [
                ...prev,
                { index: newIndex, email: '', phoneNumber: '', name: '', validEmail: false, validPhone: true },
            ])
        }
        onStudentRowsChanged(studentRowsToAdd)
    }, [studentRowsToAdd])

    const handleAddStudentRow = (index) => {
        const currentIndex = index ?? studentRowsToAdd.length
        const newStudentRow = { index: currentIndex, email: '', phoneNumber: '', name: '', validEmail: false, validPhone: true }
        setStudentRowsToAdd([...studentRowsToAdd, newStudentRow])
    }

    const handleUpdateStudentRowEmail = (index, email) => {
        const updatedStudentRows = studentRowsToAdd.map((user) => {
            if (user.index === index) {
                return {
                    ...user,
                    email: email,
                    validEmail: EmailValidator.validate(email),
                }
            }
            return user
        })
        setStudentRowsToAdd(updatedStudentRows)
    }

    const handleUpdateStudentRowPhone = (index, phone) => {
        const cleaned = phone.replace(/[\s-]/g, '')
        const normalized = cleaned && validatePhoneNumber(cleaned) ? normalizePhoneNumber(cleaned) : phone
        const updatedStudentRows = studentRowsToAdd.map((user) => {
            if (user.index === index) {
                return {
                    ...user,
                    phoneNumber: normalized,
                    validPhone: validatePhoneNumber(normalized),
                }
            }
            return user
        })
        setStudentRowsToAdd(updatedStudentRows)
    }

    const handleUpdateStudentRowName = (index, name) => {
        const updatedStudentRows = studentRowsToAdd.map((user) => {
            if (user.index === index) {
                return { ...user, name: name }
            }
            return user
        })
        setStudentRowsToAdd(updatedStudentRows)
    }

    const parsePastedEmails = (input) => {
        const possibleString = input.replace(/;/, ' ').replace(/,/, ' ')
        return possibleString.trim().split(/\s+/).filter(Boolean)
    }

    const handlePastedMultipleEmails = (index, firstValue, otherValues) => {
        const updatedStudentRows = studentRowsToAdd.map((user) => {
            if (user.index === index) {
                return {
                    ...user,
                    email: firstValue,
                    validEmail: EmailValidator.validate(firstValue),
                }
            }
            return user
        })
        let otherIndex = updatedStudentRows.length
        for (const otherVal of otherValues) {
            updatedStudentRows.push({
                index: otherIndex,
                email: otherVal,
                phoneNumber: '',
                name: '',
                validEmail: EmailValidator.validate(otherVal),
                validPhone: true,
            })
            otherIndex++
        }
        setStudentRowsToAdd(updatedStudentRows)
    }

    const handleChangeEmailRow = (index, value) => {
        const possibleValues = parsePastedEmails(value)
        if (possibleValues.length > 1) {
            const validValues = possibleValues.filter((v) => EmailValidator.validate(v))
            if (validValues.length > 1) {
                handlePastedMultipleEmails(index, validValues[0], validValues.slice(1))
            } else {
                handleUpdateStudentRowEmail(index, value)
            }
        } else {
            handleUpdateStudentRowEmail(index, value)
        }
    }

    const handleChangePhoneRow = (index, value) => {
        handleUpdateStudentRowPhone(index, value)
    }

    const handleChangeNameRow = (index, name) => {
        handleUpdateStudentRowName(index, name)
    }

    const handleDeleteUserRow = (index) => {
        const updatedStudentRows = studentRowsToAdd.filter((user) => user.index !== index)
        let newIndex = 0
        for (const user of updatedStudentRows) {
            user.index = newIndex
            newIndex++
        }
        setStudentRowsToAdd(updatedStudentRows)
    }

    const addUserBlock = (userDto, index) => {
        return (
            <div style={{ width: '100%', display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'flex-start' }}>
                <TextField
                    value={userDto.email}
                    onClick={() => setRowInFocus(index)}
                    onChange={(event) => handleChangeEmailRow(index, event.target.value)}
                    label={<FormattedMessage id="globalwords.email" defaultMessage="Email" />}
                    style={{
                        height: '55px',
                        flex: 3,
                        minWidth: '200px',
                        borderRadius: '10px',
                        border: userDto.validEmail ? '2px dashed #80D387' : '',
                        borderColor: userDto.validEmail ? CapeeshColors.GreenBright : CapeeshColors.GreenBright,
                    }}
                    InputProps={{ disableUnderline: true }}
                    variant="filled"
                />

                <TextField
                    value={userDto.phoneNumber || ''}
                    onClick={() => setRowInFocus(index)}
                    onChange={(event) => handleChangePhoneRow(index, event.target.value)}
                    label={
                        <FormattedMessage
                            id="dashboard.invite.phone.optional"
                            defaultMessage="Phone (optional)"
                        />
                    }
                    placeholder="+4712345678"
                    error={!userDto.validPhone}
                    helperText={!userDto.validPhone ? 'Invalid phone number' : ''}
                    style={{
                        height: '55px',
                        flex: 2,
                        minWidth: '150px',
                        borderRadius: '10px',
                    }}
                    InputProps={{ disableUnderline: true }}
                    variant="filled"
                />

                <TextField
                    value={userDto.name}
                    onClick={() => setRowInFocus(index)}
                    onChange={(event) => handleChangeNameRow(index, event.target.value)}
                    label={<FormattedMessage id="globalwords.name.optional" defaultMessage="Name (optional)" />}
                    style={{ height: '55px', flex: 2, minWidth: '150px', borderRadius: '10px' }}
                    InputProps={{ disableUnderline: true }}
                    variant="filled"
                />

                {studentRowsToAdd.length > 1 && (
                    <IconButton
                        aria-label="delete"
                        style={{ marginTop: '8px' }}
                        onClick={() => handleDeleteUserRow(index)}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                )}
            </div>
        )
    }

    return (
        <div
            style={{
                display: 'flex',
                paddingLeft: '16px',
                paddingRight: '16px',
                flexDirection: 'column',
                gap: '16px',
                paddingBottom: '16px',
            }}>
            {showTitle && (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '10px',
                    }}>
                    <DialogTitle id="form-dialog-title">{title}</DialogTitle>
                    <DialogContentText>{description}</DialogContentText>
                </div>
            )}
            {studentRowsToAdd.map((userDto, index) => addUserBlock(userDto, index))}
        </div>
    )
}

MultipleStudentAddComponent.propTypes = {}

const mapStateToProps = (state) => {
    return {
        organization: state.organization,
    }
}

const mapDispatchToProps = (dispatch) => {
    return {}
}

export default connect(mapStateToProps, mapDispatchToProps)(MultipleStudentAddComponent)
